// Options service — fetches countries, cities, field options, and opportunity types from the BE.
// HTTP and ApiError mapping come from the shared apiClient; catalogs (countries/states/cities)
// are public, the rest require auth.

import { apiRequest, apiRequestData } from '../http/apiClient';

export interface SelectOption {
  label: string;
  value: string;
}

// BE wraps all responses as { success: boolean, data: T }
interface WrappedResponse<T> {
  success: boolean;
  data: T;
}

function unwrap<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  const wrapped = raw as WrappedResponse<T[]>;
  return Array.isArray(wrapped?.data) ? wrapped.data : [];
}

// GET /api/v1/options/countries — returns full country list
export async function fetchCountries(): Promise<SelectOption[]> {
  return unwrap<SelectOption>(await apiRequest('/api/v1/options/countries', { auth: false }));
}

// GET /api/v1/options/regions — requires auth, returns regions + country codes valid for opportunities
export async function fetchRegions(): Promise<SelectOption[]> {
  return unwrap<SelectOption>(await apiRequest('/api/v1/options/regions'));
}

// ── Auth-required endpoints ───────────────────────────────────────────────────

export interface FieldOption {
  label: string;
  value: string;
}

interface RawFieldOption {
  value: string;
  label: string | Record<string, string>;
}

export interface OpportunityTypeOption {
  value: string;
  label: string;
}

export interface OpportunityTypeGroup {
  group: string;
  options: OpportunityTypeOption[];
}

interface RawOpportunityTypeGroup {
  group: Record<string, string>;
  options: Array<{ value: string; label: Record<string, string> }>;
}

function resolveI18nLabel(label: string | Record<string, string>, lang: string): string {
  if (typeof label === 'string') return label;
  return label[lang] ?? label['en'] ?? label['es'] ?? Object.values(label)[0] ?? '';
}

// GET /api/v1/options/field-options?fieldName=<field> — requires auth
export async function fetchFieldOptions(fieldName: string, lang = 'es'): Promise<FieldOption[]> {
  const raw = unwrap<RawFieldOption>(
    await apiRequest(
      `/api/v1/options/field-options?fieldName=${encodeURIComponent(fieldName)}&lang=${lang}`
    )
  );
  return raw.map((o) => ({ value: o.value, label: resolveI18nLabel(o.label, lang) }));
}

// GET /api/v1/options/opportunity-types — requires auth, grouped with i18n labels
export async function fetchOpportunityTypes(lang = 'es'): Promise<OpportunityTypeGroup[]> {
  const raw = await apiRequestData<RawOpportunityTypeGroup[]>('/api/v1/options/opportunity-types');
  return raw.map((g) => ({
    group: resolveI18nLabel(g.group, lang),
    options: g.options.map((o) => ({ value: o.value, label: resolveI18nLabel(o.label, lang) })),
  }));
}

export interface EligibilityOption {
  value: string;
  label: string;
}

export interface EligibilityGroup {
  group: string;
  options: EligibilityOption[];
}

interface RawEligibilityGroup {
  group: Record<string, string>;
  options: Array<{ value: string; label: Record<string, string> }>;
}

// GET /api/v1/options/eligibility — requires auth, grouped with i18n labels (4 categories, 29 tags)
export async function fetchEligibility(lang = 'es'): Promise<EligibilityGroup[]> {
  const raw = await apiRequestData<RawEligibilityGroup[]>('/api/v1/options/eligibility');
  return raw.map((g) => ({
    group: resolveI18nLabel(g.group, lang),
    options: g.options.map((o) => ({ value: o.value, label: resolveI18nLabel(o.label, lang) })),
  }));
}

// GET /api/v1/options/states?iso2=AR,MX — returns states/provinces for given country codes
export async function fetchStates(iso2Codes: string[]): Promise<SelectOption[]> {
  if (iso2Codes.length === 0) return [];
  const params = iso2Codes.join(',');
  return unwrap<SelectOption>(
    await apiRequest(`/api/v1/options/states?iso2=${encodeURIComponent(params)}`, { auth: false })
  );
}

// GET /api/v1/options/cities?iso2=AR,MX[&state=Antioquia] — returns deduplicated cities for given
// country codes, optionally narrowed to a single state/province (BE only honors `state` when a
// single iso2 code is passed — see OptionsController#getCities).
export async function fetchCities(iso2Codes: string[], state?: string): Promise<SelectOption[]> {
  if (iso2Codes.length === 0) return [];
  const params = iso2Codes.join(',');
  const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
  return unwrap<SelectOption>(
    await apiRequest(`/api/v1/options/cities?iso2=${encodeURIComponent(params)}${stateParam}`, {
      auth: false,
    })
  );
}
